/* eslint-disable react-hooks/exhaustive-deps */
/* global webkit, login, register */
import React, { useEffect, createRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';

// import components
import { withEnvironmentContext } from '@embeds/core/EnvironmentContext';
import StandardContent from '@embeds/common/Registration/StandardRegistration';
import PodcastContent from '@cms/common/Registration/OverlayContent/PodcastCustom';
import Lightbox from '@embeds/common/Lightbox/Lightbox';

// import utils
import eventTracking, { generateTrackingValue, linkTracking } from '@utils/analytics/index';
import { createCustomEventObject, createEventToken } from '@utils/embeds/common/events';

const mapSocialNetworkName = (network) => {
  const networks = {
    'google-oauth2': 'google'
  };
  return networks[network] || network;
};

const RegistrationOverlay = ({ data, envData }) => {
  const { customSourceCode } = data;
  // create ref and states
  const contentRef = createRef();
  const formRef = createRef();
  const testButtonRef = createRef();

  const sourceCode = useMemo(() => {
    return customSourceCode?.[envData.registration.site.id] ? customSourceCode[envData.registration.site.id] : envData.registration.site.sourceCode;
  }, [ customSourceCode, envData.registration.site ]);


  const [ isOpen, setOpen ] = useState(envData.isLocked);
  // const [ isLoading, setLoading ] = useState(isOpen)
  const [ showSuccess, setShowSuccess ] = useState(false);
  const [ showForm, setShowForm ] = useState(false);

  const [ token ] = useState(createEventToken(envData.capiId, envData.newsKey));
  // const [ minHeight, setMinHeight ] = useState(50);

  const [ unlockEvent ] = useState( createCustomEventObject({
    name: 'et:overlay:unlock:all',
    el: document,
    detail: { token, id: data.id, type: data.type, el: envData.rootEl },
    handler: (e) => {
      if (e.detail.token === token) {
        console.log('tokens match, unlock widgets', data.id, data.type, token);
        // fire registration complete function to unlock this widget
        envData.registration.handleComplete();
        // close the overlay if is not the current widget ( current widget shows registration success message )
        // if (e.detail.id !== data.id) setOpen(false);
        console.log('check source element', e.detail.el, envData.rootEl, e.detail.el !== envData.rootEl);
        if (e.detail.el !== envData.rootEl) setOpen(false);

        // update the min height of root el and overlay
        // setMinHeight(0);
        envData._updateMinHeight(0);
      } else {
        console.warn('tokens dont match - wtf.', e.detail.token, token);
      }
      // console.log('et:overlay:unlock:all', e, data.type, data.id)
      // console.log('token', token, e.detail.token, window.atob(token), window.atob(e.detail.token))
    }
  }) );

  const initCaaSLite = !window.location.href.match('bypass_caas_init');

  const [ resizeObserver ] = useState(new ResizeObserver( ([ entry ]) => {
    console.log('overlay form was resized.', entry);

    if (entry) {
      const contentHeight = entry.contentRect.height;
      const paddingMarginOffset = (64 + 16); //envData.device === 'mobile' ? (16 + 0) : (64 + 16)

      const height = contentHeight + paddingMarginOffset;
      console.log('height', contentHeight, height, paddingMarginOffset, envData);

      // setMinHeight(height);
      envData._updateMinHeight(height);
    }
  }));

  // Function to call registration tracking
  function registrationTracking(labelText, options = {}) {
    const label = generateTrackingValue(data, labelText, { eventTextPrefix: true, delimiter: '-' });
    linkTracking(envData, data, label, 1, {
      container: 'global-sign-in',
      ...options
    } );
  }

  function addCaaSLiteEvents(caasLite, currentFormRef) {

    const events = [
      {
        name: 'EVENT_CAAS_SOCIAL_BUTTON_CLICKED',
        handler: (network) => {
          console.log('EVENT_CAAS_SOCIAL_BUTTON_CLICKED', network);
          const labelPrefix = network ? `${ mapSocialNetworkName(network.name) }-register` : 'register';
          registrationTracking(labelPrefix, { events: [ 'event10', 'event5' ] });
        }
      },
      {
        name: 'EVENT_CAAS_LOADED',
        handler: (e) => {
          console.log('EVENT_CAAS_LOADED', e);
        }
      },
      {
        name: 'EVENT_REGISTRATION_COMPLETE',
        handler: (e) => {
          console.log('EVENT_REGISTRATION_COMPLETE - close caasLite, fire tracking event, show success message, set registration complete state in overlay, and dispatch unlocking event for other widgets.', e);
          // close caasLite
          if (initCaaSLite) {
            caasLite.close();
          }

          // Fire complete registration tracking
          // registrationTracking('register', { events: [ 'event10', 'event14' ] });

          // Show Success message
          setShowSuccess(true);

          // fire registration complete function to unlock this widget
          envData.registration.handleComplete();

          // dispatch unlock event to unlock other widgets on page.
          unlockEvent.el.dispatchEvent(unlockEvent.event);
        }
      },
      {
        name: 'TEMP_EVENT_CAAS_LOGIN_BUTTON_CLICK',
        handler: (e) => {
          console.log('TEMP_EVENT_CAAS_LOGIN_BUTTON_CLICK', e);
          registrationTracking('login', { events: [ 'event10', 'event4' ] });
        }
      },
      // TEMP - ADD LISTENER FOR CLICK EVENTS IN FORM FOR ELEMENTS THAT DONT EXIST YET
      // It will fire the same event handler in CaaS, and can be removed once CaaS emits these itself.
      {
        el: currentFormRef,
        name: 'click',
        handler: (e) => {
          const target = e.target;
          console.log(e, e.target, e.currentTarget);

          if (target.classList.contains('caas-login-button-base')) {
            const regEvent = events.find(ev => ev.name === 'TEMP_EVENT_CAAS_LOGIN_BUTTON_CLICK');
            regEvent && regEvent.handler();
          }
        }
      }
    ];

    // TODO: this test button ref has gotta go.
    if (testButtonRef.current && contentRef.current) {
      const testEventobj = {
        el: contentRef.current,
        name: 'et:overlay:test:registration:complete',
        handler: events.find( ev => ev.name === 'EVENT_REGISTRATION_COMPLETE' ).handler
      };

      const testEvent = new CustomEvent( testEventobj.name, {
        bubbles: true,
        detail: {
          foo: 'complete'
        }
      });

      testButtonRef.current.addEventListener('click', (e) => e.target.dispatchEvent(testEvent) );

      events.push(testEventobj);
    }


    // add a listener for each event to the element or caaslite.
    events.forEach( ({ el, name, handler }) => el ? el.addEventListener(name, handler) : caasLite.on(name, handler) );

    // return events so they can be remove when unmounting overlay component.
    return events;
  }

  // Function to Close the overlay
  function handleClose() { setOpen(false); }

  // side effect for componentDidMount to listen for global unlock event
  useEffect( () => {
    const { el, name, handler } = unlockEvent;

    el.addEventListener(name, handler);

    return () => el.removeEventListener(name, handler);
  }, []);

  // side effect to change open status based on widget in locked state.
  useEffect(() => {
    // console.log('registration overlay: isLocked state change', envData.isLocked, data.id, data.type, isOpen, )
    if (envData.isLocked) setOpen(envData.isLocked);
    if (isOpen && !envData.isLocked) {
      console.log('DSET-225 - is open and no longer locked - wtf', envData.isLocked, data.id, data.type, isOpen, );
      // setOpen(false);
    }
  }, [ envData.isLocked ]);

  useEffect(() => {
    const depsLoaded = envData.registration.depsLoaded; // (envData.registration.caasLoaded && envData.registration.rampartLoaded)
    // console.log('overlay state change', isOpen, formRef.current, depsLoaded, envData.type, envData.isLocked, envData)

    if (isOpen && depsLoaded) {
      if (envData.enteredViewport) {
        // registrationTracking('pop-up', { events: [ 'event157' ] });
        const enteredViewportEvent = data.type === 'podcast' ? [ 'event219' ] : [ 'event157' ];
        eventTracking({
          events: enteredViewportEvent,
          eVar23: generateTrackingValue(data, 'global-sign-in|1|pop-up', { eventTextPrefix: true, delimiter: '-' }),
        }, envData);
      }
    }

  }, [ isOpen, envData.enteredViewport ]);
  // side effect to call CCAPI and analytics when open state is triggered.
  useEffect(() => {
    const currentFormRef = formRef.current;
    const depsLoaded = envData.registration.depsLoaded; // (envData.registration.caasLoaded && envData.registration.rampartLoaded)

    // console.log('showForm state change', showForm, isOpen, formRef.current, depsLoaded, envData.type)
    if (showForm && currentFormRef && depsLoaded) {

      const config = {
        masthead: envData.registration.site.caasId,
        showLogo: false,
        placeHolderElement: currentFormRef,
        sourceCode, //: customSourceCode?.[envData.registration.site.id] ? customSourceCode[envData.registration.site.id] : envData.registration.site.sourceCode,
        analytics: {
          registerCompleteEvar23: generateTrackingValue(data, 'global-sign-in|1|register', { eventTextPrefix: true, delimiter: '-' })
        }
      };

      console.log('caas config', config);
      // create a reference to the current contentref so we can use it to unobserve in the cleanup function
      const currentContentRef = contentRef.current;

      // create instance of CaaSLite and attach event handlers
      console.log('window.CaasLite', window.CaasLite);
      const caasLite = new window.CaasLite(config);
      const caasLiteEvents = addCaaSLiteEvents(caasLite, currentFormRef);

      console.log('caaslite config object and events object', config, caasLiteEvents);

      // init caasLite and create a resize observer
      if (initCaaSLite) {
        caasLite.init().then( (caasLiteForm) => {
          console.log('caasLiteForm is loaded now! create a resize observer to monitor form height changes', caasLiteForm);
          resizeObserver.observe(currentContentRef);
        });
      } else {
        console.log('Editorial tools: Didnt init CaaSLite due to url param');
      }

      return () => {
        // cleanup function for side effect, remove event listeners and observers
        caasLiteEvents.forEach( ({ el, name, handler }) => el && el.removeEventListener(name, handler) );
        // unobserve the resize observer
        currentContentRef && resizeObserver.unobserve(currentContentRef);
      };
    }

  }, [ showForm ]);


  const handleRegister = () => {
    registrationTracking('register', { events: [ 'event10', 'event13' ] });

    if (envData.site !== 'newscomau') {
      if (!window.Rampart) return console.error('window.Rampart not available');
      const rampartConfig = { site: envData.registration.site.caasId, sourceCode };
      const rampart = new window.Rampart(rampartConfig);

      console.log('is skynews rampart instance', rampartConfig, rampart, window.Rampart);
      rampart.signup(window.location.href);

      return;
    }


    if (envData.isWeb || envData.isAmp) {
      console.log('registration overlay handleRegister WEB');
      setShowForm(true);
    }

    if (envData.isApp) {
      console.log('registration overlay handleRegister APP');
      const { id, type } = envData;
      const payload = { id, type };

      if (envData.platform === 'ios') {
        try {
          webkit.messageHandlers.sendNativeRegisterAction.postMessage(payload);
        } catch (err) {
          console.error('no native context exists for webkit.messageHandlers.sendNativeRegisterAction', err);
        }
      }

      if (envData.platform === 'android') {
        try {
          register.sendNativeRegisterAction(payload);
        } catch (err) {
          console.error('no native context exists for register.sendNativeRegisterAction', err);
        }
      }
    }
  };

  const handleLogin = () => {
    registrationTracking('login', { events: [ 'event10' ] });

    if (envData.isApp) {
      console.log('registration overlay handleLogin APP');
      const { id, type } = envData;
      const payload = { id, type };

      if (envData.platform === 'ios') {
        try {
          webkit.messageHandlers.sendNativeLoginAction.postMessage(payload);
        } catch (err) {
          console.error('no native context exists for webkit.messageHandlers.sendNativeLoginAction', err);
        }
      }

      if (envData.platform === 'android') {
        try {
          login.sendNativeLoginAction(payload);
        } catch (err) {
          console.error('no native context exists for login.sendNativeLoginAction', err);
        }
      }
    }

    if (envData.isWeb) {
      console.log('registration overlay handleLogin WEB');
      const rampartConfig = { site: envData.registration.site.caasId };
      const rampart = new window.Rampart(rampartConfig);
      console.log('rampart login', rampartConfig, rampart, window.Rampart);
      rampart.login(window.location.href);
    }

    if (envData.isAmp) {
      console.log('registration overlay handleLogin AMP');
      const envPrefix = (envData.env && envData.env !== 'prod') ? envData.env + '.' : '';
      const loginUrl = `https://${ envPrefix }commerceapi.news.com.au/identity-amp/login/${ envData.registration.site.caasId }/READER_ID/?return=${ envData.capiUrl }`;
      window.open(loginUrl, '_blank');
    }
  };

  // generate classnames
  const stateClassname = `et-common_registration_state_${ showForm ? 'form' : 'intro' }`;
  const introPositionClassname = `et-common_registration_intro_position_${ (data.overlayIntroAlignment || 'top') }`;
  const visibilityClassname = envData.enteredViewport ? 'et-common_registration_viewport' : '';

  const isPodcast = data.type === 'podcast';

  return (
    <>
      <div
        isopen={`${ isOpen }`}
        islocked={`${ envData.isLocked }`}
        registrationlocked={`${ envData.registration.registrationLocked }`}
        rampartloaded={`${ envData.registration.rampartLoaded }`}
        caasloaded={`${ envData.registration.caasLoaded }`}
        depsloaded={`${ envData.registration.depsLoaded }`}
      />

      {isOpen && isPodcast && (
        <div
          device={`${ envData.device }`.toLowerCase()}
          className={classNames([
            'et-common_registration',
            `et-common_registration_theme_${ envData.theme }`,
            `et-common_registration_type_${ envData.type }`,
            stateClassname,
            introPositionClassname,
            visibilityClassname
          ])}
        >
          <div className="et-common_registration_overlay"></div>
          <div className={classNames([ 'et-common_registration_content' ])} ref={contentRef} >
            <PodcastContent
              data={data}
              envData={envData}
              handleLogin={handleLogin}
              registrationTracking={registrationTracking}
            />
          </div>
        </div>
      )}

      {isOpen && !isPodcast && (
        <Lightbox isCloseable={false} ref={contentRef} verticalAlign={data.overlayIntroAlignment}>
          <StandardContent
            type={data.type}
            isLocked={envData.isLocked}
            siteId={envData.site}
            showForm={showForm}
            showSuccess={showSuccess}

            formRef={formRef}
            testButtonRef={testButtonRef}

            handleRegister={handleRegister}
            handleLogin={handleLogin}
            handleClose={handleClose}
          />
        </Lightbox>
      )}
    </>
  );
};

RegistrationOverlay.propTypes = {
  data: PropTypes.object.isRequired,
  envData: PropTypes.object.isRequired,
};

RegistrationOverlay.defaultProps = {
  data: {},
  envData: {}
};

export default withEnvironmentContext(React.memo(RegistrationOverlay));
